/**
 * Payments UI State Slice
 * Handles LOCAL UI state for payments feature (not API calls)
 * API calls are handled by features/payments/paymentsApiSlice.ts
 */

import { createSlice, PayloadAction, createSelector } from "@reduxjs/toolkit";

export interface PaymentsUIState {
  // Selected payment for detail view
  selectedPaymentId: string | null;

  // Search/filter state
  searchQuery: string;
  filterByStatus: "all" | "success" | "failed" | "pending";
  filterByMethod: "all" | "upi" | "card" | "cash";
  filterByVehicleType: "all" | "car" | "bike";

  // Amount range filter
  amountRange: {
    min: number | null;
    max: number | null;
  };

  // UI preferences
  viewMode: "table" | "cards";
  sortBy: "ticketDateTime" | "amount" | "vehicleNumber" | "pgStatus";
  sortOrder: "asc" | "desc";

  // Pagination state
  currentPage: number;
  itemsPerPage: number;

  // Stats view preferences
  showStats: boolean;
  statsTimeframe: "daily" | "weekly" | "monthly";
}

const initialState: PaymentsUIState = {
  selectedPaymentId: null,
  searchQuery: "",
  filterByStatus: "all",
  filterByMethod: "all",
  filterByVehicleType: "all",
  amountRange: {
    min: null,
    max: null,
  },
  viewMode: "table",
  sortBy: "ticketDateTime",
  sortOrder: "desc",
  currentPage: 1,
  itemsPerPage: 10,
  showStats: true,
  statsTimeframe: "daily",
};

const paymentsSlice = createSlice({
  name: "payments",
  initialState,
  reducers: {
    // Selection
    setSelectedPayment: (state, action: PayloadAction<string | null>) => {
      state.selectedPaymentId = action.payload;
    },

    // Search and filters
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
      state.currentPage = 1;
    },

    setFilterByStatus: (
      state,
      action: PayloadAction<"all" | "success" | "failed" | "pending">
    ) => {
      state.filterByStatus = action.payload;
      state.currentPage = 1;
    },

    setFilterByMethod: (
      state,
      action: PayloadAction<"all" | "upi" | "card" | "cash">
    ) => {
      state.filterByMethod = action.payload;
      state.currentPage = 1;
    },

    setFilterByVehicleType: (
      state,
      action: PayloadAction<"all" | "car" | "bike">
    ) => {
      state.filterByVehicleType = action.payload;
      state.currentPage = 1;
    },

    setAmountRange: (
      state,
      action: PayloadAction<{ min: number | null; max: number | null }>
    ) => {
      state.amountRange = action.payload;
      state.currentPage = 1;
    },

    clearAmountRange: (state) => {
      state.amountRange = { min: null, max: null };
      state.currentPage = 1;
    },

    // View preferences
    setViewMode: (state, action: PayloadAction<"table" | "cards">) => {
      state.viewMode = action.payload;
    },

    setSortBy: (
      state,
      action: PayloadAction<
        "ticketDateTime" | "amount" | "vehicleNumber" | "pgStatus"
      >
    ) => {
      state.sortBy = action.payload;
    },

    setSortOrder: (state, action: PayloadAction<"asc" | "desc">) => {
      state.sortOrder = action.payload;
    },

    toggleSortOrder: (state) => {
      state.sortOrder = state.sortOrder === "asc" ? "desc" : "asc";
    },

    // Pagination
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },

    setItemsPerPage: (state, action: PayloadAction<number>) => {
      state.itemsPerPage = action.payload;
      state.currentPage = 1;
    },

    // Stats preferences
    toggleStats: (state) => {
      state.showStats = !state.showStats;
    },

    setStatsTimeframe: (
      state,
      action: PayloadAction<"daily" | "weekly" | "monthly">
    ) => {
      state.statsTimeframe = action.payload;
    },

    // Reset all filters
    resetFilters: (state) => {
      state.searchQuery = "";
      state.filterByStatus = "all";
      state.filterByMethod = "all";
      state.filterByVehicleType = "all";
      state.amountRange = { min: null, max: null };
      state.currentPage = 1;
    },
  },
});

export const {
  setSelectedPayment,
  setSearchQuery,
  setFilterByStatus,
  setFilterByMethod,
  setFilterByVehicleType,
  setAmountRange,
  clearAmountRange,
  setViewMode,
  setSortBy,
  setSortOrder,
  toggleSortOrder,
  setCurrentPage,
  setItemsPerPage,
  toggleStats,
  setStatsTimeframe,
  resetFilters,
} = paymentsSlice.actions;

/**
 * Memoized selectors for payments UI state
 * Use createSelector for optimal performance
 */

// Base selector
const selectPaymentsState = (state: { payments: PaymentsUIState }) =>
  state.payments;

// Memoized selectors
export const selectPaymentsUI = createSelector(
  [selectPaymentsState],
  (payments) => payments
);

export const selectSelectedPaymentId = createSelector(
  [selectPaymentsState],
  (payments) => payments.selectedPaymentId
);

export const selectSearchQuery = createSelector(
  [selectPaymentsState],
  (payments) => payments.searchQuery
);

export const selectPaymentsFilters = createSelector(
  [selectPaymentsState],
  (payments) => ({
    searchQuery: payments.searchQuery,
    filterByStatus: payments.filterByStatus,
    filterByMethod: payments.filterByMethod,
    filterByVehicleType: payments.filterByVehicleType,
    amountRange: payments.amountRange,
  })
);

export const selectHasActiveFilters = createSelector(
  [selectPaymentsState],
  (payments) =>
    payments.searchQuery !== "" ||
    payments.filterByStatus !== "all" ||
    payments.filterByMethod !== "all" ||
    payments.filterByVehicleType !== "all" ||
    payments.amountRange.min !== null ||
    payments.amountRange.max !== null
);

export const selectViewMode = createSelector(
  [selectPaymentsState],
  (payments) => payments.viewMode
);

export const selectSorting = createSelector(
  [selectPaymentsState],
  (payments) => ({
    sortBy: payments.sortBy,
    sortOrder: payments.sortOrder,
  })
);

export const selectPaymentsPagination = createSelector(
  [selectPaymentsState],
  (payments) => ({
    currentPage: payments.currentPage,
    itemsPerPage: payments.itemsPerPage,
  })
);

export const selectStatsPreferences = createSelector(
  [selectPaymentsState],
  (payments) => ({
    showStats: payments.showStats,
    timeframe: payments.statsTimeframe,
  })
);

/**
 * Composite selector for all payments UI state
 */
export const selectCompletePaymentsUI = createSelector(
  [selectPaymentsState],
  (payments) => ({
    selectedPaymentId: payments.selectedPaymentId,
    searchQuery: payments.searchQuery,
    filters: {
      status: payments.filterByStatus,
      method: payments.filterByMethod,
      vehicleType: payments.filterByVehicleType,
      amountRange: payments.amountRange,
    },
    viewMode: payments.viewMode,
    sorting: {
      sortBy: payments.sortBy,
      sortOrder: payments.sortOrder,
    },
    pagination: {
      currentPage: payments.currentPage,
      itemsPerPage: payments.itemsPerPage,
    },
    stats: {
      showStats: payments.showStats,
      timeframe: payments.statsTimeframe,
    },
  })
);

// Get list of active filter types (for showing filter chips)
export const selectActiveFilters = createSelector(
  [selectPaymentsState],
  (payments) => {
    const active: string[] = [];
    if (payments.searchQuery) active.push("search");
    if (payments.filterByStatus !== "all") active.push("status");
    if (payments.filterByMethod !== "all") active.push("method");
    if (payments.filterByVehicleType !== "all") active.push("vehicleType");
    if (payments.amountRange.min !== null || payments.amountRange.max !== null)
      active.push("amountRange");
    return active;
  }
);

// Get filter count (for badge display)
export const selectActiveFilterCount = createSelector(
  [selectActiveFilters],
  (filters) => filters.length
);

export default paymentsSlice.reducer;
